import { replyComment } from '@/api/sale-api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Loader2, SendHorizonal, X } from 'lucide-react';
import { KeyboardEvent, useState } from 'react';

interface ReplyComponentProps {
  authorName: string;
  commentId: string;
  onClose: () => void;
  compact?: boolean;
}

export default function ReplyComponent({
  authorName,
  commentId,
  onClose,
  compact = false,
}: ReplyComponentProps) {
  const [replyValue, setReplyValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSendReply = async () => {
    if (!replyValue.trim() || submitting) return;

    setSubmitting(true);
    try {
      const resp = await replyComment(commentId, replyValue);
      if (resp.success) {
        setReplyValue('');
        toast({ title: 'Success', description: 'Reply sent successfully', variant: 'default' });
        onClose();
        return;
      }

      toast({
        title: 'Error',
        description: resp.message || 'Failed to send reply',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      void handleSendReply();
    }
  };

  return (
    <div
      className={cn(
        'w-full min-w-0 animate-in fade-in slide-in-from-top-1 duration-200',
        compact ? 'mt-1.5' : 'mt-2',
      )}
    >
      <div
        className={cn(
          'border border-blue-100 bg-slate-50/90',
          compact ? 'rounded-md p-2' : 'rounded-lg p-2.5 sm:p-3',
        )}
      >
        <div className={cn('flex items-start justify-between gap-1.5', compact ? 'mb-1.5' : 'mb-2')}>
          <p
            className={cn(
              'min-w-0 flex-1 truncate leading-snug text-slate-600',
              compact ? 'text-[10px]' : 'text-xs',
            )}
          >
            Replying to <span className="font-semibold text-slate-900">{authorName}</span>
          </p>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Cancel reply"
            className={cn(
              'shrink-0 rounded-full text-slate-400 hover:bg-red-50 hover:text-red-500',
              compact ? 'h-6 w-6' : 'h-7 w-7',
            )}
          >
            <X className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
          </Button>
        </div>

        <Textarea
          value={replyValue}
          onChange={(event) => setReplyValue(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Write a reply…"
          disabled={submitting}
          className={cn(
            'w-full resize-none rounded-md border-slate-200 bg-white shadow-none focus-visible:ring-1 focus-visible:ring-blue-200',
            compact
              ? 'min-h-[52px] px-2 py-1.5 text-xs'
              : 'min-h-[64px] px-2.5 py-2 text-sm sm:min-h-[72px]',
          )}
        />

        <div className={cn('flex items-center justify-end gap-2', compact ? 'mt-1.5' : 'mt-2')}>
          {!compact && (
            <span className="hidden text-[10px] text-slate-400 sm:inline">Ctrl+Enter to send</span>
          )}
          <Button
            onClick={() => void handleSendReply()}
            disabled={!replyValue.trim() || submitting}
            size={compact ? 'sm' : 'default'}
            className={cn(compact && 'h-7 gap-1.5 px-2.5 text-xs')}
          >
            {submitting ? (
              <Loader2 className={cn('animate-spin', compact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
            ) : (
              <SendHorizonal className={cn(compact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
            )}
            <span>{compact ? 'Send' : 'Reply'}</span>
            {!compact && <span className="hidden sm:inline"> as comment</span>}
          </Button>
        </div>
      </div>
    </div>
  );
}
